import { createContext, useContext } from 'react'
import type { RulesSectionId } from './content'

/**
 * Rules navigation, app-wide.
 *
 * A context rather than props, which is unusual here and deliberate: six entry points sit on four
 * different screens, several of them several components deep, and threading an `onOpenRules`
 * through `TabPage` → `GameweekGrid` → `GameEditScreen` would touch a dozen files that have
 * nothing to do with the rules.
 *
 * The screen state lives here too, so `AppRoutes` reads one flag alongside `isViewingSettings`
 * instead of owning a second navigation concept. There is still no URL router: opening the screen
 * at a section is an argument, not a hash.
 *
 * The context and its hooks live apart from `RulesProvider` so that file exports a component and
 * nothing else — the repo's fast-refresh rule.
 */
export interface RulesController {
  /** Section currently shown in the ⓘ sheet, or null. */
  sheetSection: RulesSectionId | null
  openSheet: (id: RulesSectionId) => void
  closeSheet: () => void
  /** True while the how-to-play screen should be rendered over the tab shell. */
  isScreenOpen: boolean
  /** The section the screen should land on, or null for the top. */
  screenSection: RulesSectionId | null
  openScreen: (id?: RulesSectionId) => void
  closeScreen: () => void
}

export const RulesContext = createContext<RulesController | null>(null)

/**
 * The full controller, for the route switch. Throws without a provider on purpose: `AppRoutes`
 * renders the how-to-play screen off this, so a missing provider there is a broken app, not a
 * degraded one — and the failure should be loud and immediate rather than a route that never opens.
 */
export function useRules(): RulesController {
  const controller = useContext(RulesContext)
  if (!controller) {
    throw new Error('useRules must be used inside a RulesProvider')
  }
  return controller
}

/**
 * Opening-only, and tolerant of no provider.
 *
 * The ⓘ affordances live on leaf components — a league row, a chip modal, the banker toggle — and
 * every one of those is rendered on its own in a test. Making those hooks throw would mean wrapping
 * a dozen unrelated test files in a provider to assert things that have nothing to do with rules.
 * Outside a provider the button renders and does nothing, which is the right degradation for a
 * secondary affordance.
 */
function useRulesOpeners(): Pick<RulesController, 'openSheet' | 'openScreen'> {
  const controller = useContext(RulesContext)
  return {
    openSheet: controller?.openSheet ?? noopOpen,
    openScreen: controller?.openScreen ?? noopOpen,
  }
}

const noopOpen = () => {}

/** The narrow hook the ⓘ affordances use — `open(id)` and nothing else. */
export function useRulesSheet(): { open: (id: RulesSectionId) => void } {
  const { openSheet } = useRulesOpeners()
  return { open: openSheet }
}

/** The handoff the two existing info modals use in place of an ⓘ of their own. */
export function useRulesScreen(): { open: (id?: RulesSectionId) => void } {
  const { openScreen } = useRulesOpeners()
  return { open: openScreen }
}
